import "./index.css";
import {
  createAccountController,
  createRpcClient,
  createRpcTransport,
  type AccountController,
} from "@hq/client";
import { App } from "@hq/app-web";
import {
  browserNetworkSource,
  createClientContext,
  type ClientContextValue,
} from "@hq/client-react";
import { openWebStore, type OpenWebStoreOwner } from "@hq/local-store-sqlite/web";
// oxlint-disable-next-line import/default -- Vite `?worker` modules export a Worker constructor
import SqliteWorker from "@hq/local-store-sqlite/worker?worker";
import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { registerSW } from "virtual:pwa-register";
import { HeldByOtherTab } from "./HeldByOtherTab";
import { noServerTransport } from "./transport";

if (import.meta.env.DEV) {
  void import("react-grab");
}

const DB_NAME = "hq";
const root = createRoot(document.getElementById("root")!);

registerSW({ immediate: true });

function mount(owner: OpenWebStoreOwner) {
  let ownsStore = true;
  const hasServer = import.meta.env.VITE_SYNC_ENABLED === "true";
  let account: AccountController | undefined;
  const rpc = createRpcClient({
    url: `${location.origin}/api/rpc`,
    fetch: async (request, init) => {
      const response = await fetch(request, {
        ...init,
        credentials: "include",
        redirect: "manual",
      });
      if (response.status === 401 || response.type === "opaqueredirect") void account?.refresh();
      return response;
    },
  });
  const client: ClientContextValue = createClientContext({
    store: owner.store,
    transport: hasServer ? createRpcTransport(rpc) : noServerTransport,
    syncEnabled: () => ownsStore && hasServer && account?.getSnapshot().phase === "signed_in",
    network: browserNetworkSource(),
    sync: { intervalMs: 20_000 },
  });
  if (hasServer) {
    account = createAccountController({
      url: `${location.origin}/api/auth`,
      fetch,
      store: owner.store,
      sync: client.sync,
      flush: async () => {
        await Promise.all([client.autosave.flushAll(), client.taskAutosave.flushAll()]);
      },
    });
    void account.refresh();
  }
  const foreground = () => {
    if (document.visibilityState === "visible") {
      void account?.refresh();
      client.sync.kick();
    } else {
      void client.autosave.flushAll();
      void client.taskAutosave.flushAll();
    }
  };
  document.addEventListener("visibilitychange", foreground);
  const reconnect = () => {
    void account?.refresh();
  };
  window.addEventListener("online", reconnect);
  let persistenceRequested = false;
  const persist = () => {
    if (persistenceRequested || !navigator.storage.persist) return;
    persistenceRequested = true;
    void navigator.storage.persist().catch(() => false);
  };
  window.addEventListener("appinstalled", persist);
  const unsubscribeSync = client.sync.subscribe((status) => {
    if (status.lastPullAt) persist();
  });
  // Dev and smoke hook (research.md Section 28). Not a public API.
  window.__hq = { store: owner.store, client, bootMs: owner.bootMs };

  owner.onLost(() => {
    ownsStore = false;
    client.sync.stop();
    unsubscribeSync();
    document.removeEventListener("visibilitychange", foreground);
    window.removeEventListener("online", reconnect);
    window.removeEventListener("appinstalled", persist);
    root.render(<HeldByOtherTab lost />);
  });

  root.render(
    <StrictMode>
      <App
        client={client}
        hasServer={hasServer}
        {...(account ? { account, rpc } : {})}
        bootMs={owner.bootMs}
        onResetLocalData={async () => {
          await Promise.all([client.autosave.flushAll(), client.taskAutosave.flushAll()]);
          await owner.resetLocalData({ discardUnsyncedChanges: !hasServer });
          location.reload();
        }}
        onReauthenticate={async () => {
          await Promise.all([client.autosave.flushAll(), client.taskAutosave.flushAll()]);
          // The service worker answers navigations from its cache, so a reload would never reach
          // the gateway's sign-in. Without it the reload goes to the network, where Cloudflare
          // Access redirects to its login and back. The app registers the worker again on load.
          const registrations = (await navigator.serviceWorker?.getRegistrations()) ?? [];
          await Promise.all(registrations.map((registration) => registration.unregister()));
          location.reload();
        }}
        onReady={() => {
          document.documentElement.dataset.hqReady = "true";
        }}
      />
    </StrictMode>,
  );
}

async function boot() {
  const result = await openWebStore({ name: DB_NAME, createWorker: () => new SqliteWorker() });
  if (result.status === "owner") {
    mount(result);
    return;
  }
  root.render(
    <HeldByOtherTab
      onTakeOver={async () => {
        mount(await result.takeOver());
      }}
    />,
  );
}

boot().catch((err: unknown) => {
  console.error(err);
  root.render(
    <div className="flex h-full items-center justify-center p-6 text-fg">
      <div className="max-w-md space-y-2">
        <h1 className="text-lg font-semibold">Could not open local storage</h1>
        <p className="text-fg-muted text-sm">{err instanceof Error ? err.message : String(err)}</p>
        <p className="text-fg-faint text-xs">
          HQ needs a browser with Origin Private File System support (Chrome, Edge, Firefox, or
          Safari 17 and later).
        </p>
      </div>
    </div>,
  );
});
